import React, { useState } from "react";
import { GitBranch } from "lucide-react";

export default function PaymentScenarios() {
  const [price, setPrice] = useState("");
  const p = parseFloat(price) || 0;
  const currencyFormatter = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });
  const formatCurrency = (v) => (isNaN(v) ? "€ 0,00" : currencyFormatter.format(v));

  const s1_acconto = p * 0.5, s1_saldo = p * 0.5;
  const s2_total = p * 0.98, s2_discount = p - s2_total;
  const s2_acconto = s2_total * 0.7, s2_saldo = s2_total * 0.3;
  const s3_total = p * 0.95, s3_discount = p - s3_total;

  return (
    <div className="flex-1 bg-slate-50 p-6 overflow-y-auto custom-scrollbar" data-testid="scenarios-view">
      <div className="w-full bg-white rounded-2xl border border-slate-200 p-6 md:p-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-outfit font-bold text-slate-900 tracking-tight">Scenari di Pagamento</h1>
          <p className="text-slate-500 text-sm mt-1">Calcola le opzioni di pagamento per il cliente.</p>
        </div>

        <div className="mb-10 max-w-md mx-auto">
          <label className="block text-center text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">
            Prezzo di Vendita (€)
          </label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="Es. 1000"
            className="w-full p-4 text-3xl font-outfit font-bold text-center text-slate-900 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none"
            data-testid="scenarios-price-input"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {p <= 0 ? (
            <div className="lg:col-span-3 text-center text-slate-400 py-16 italic flex flex-col items-center justify-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
              <GitBranch size={64} className="text-slate-300 mb-4" strokeWidth={1.25} />
              Inserisci un prezzo per vedere le opzioni.
            </div>
          ) : (
            <>
              <ScenarioCard
                title="Standard" headerBg="bg-blue-700"
                description="Pagamento comodo diviso in due tranche."
                rows={[
                  { label: "50% Ordine", value: formatCurrency(s1_acconto) },
                  { label: "50% Consegna", value: formatCurrency(s1_saldo) },
                ]}
                totalLabel="Totale" totalValue={formatCurrency(p)} totalColor="text-blue-700"
                testId="scenario-standard"
              />
              <ScenarioCard
                title="Smart" headerBg="bg-emerald-600" badge="-2%" featured
                description="Sconto extra per un acconto maggiore."
                saving={formatCurrency(s2_discount)} savingColor="emerald"
                rows={[
                  { label: "70% Ordine", value: formatCurrency(s2_acconto) },
                  { label: "30% Consegna", value: formatCurrency(s2_saldo) },
                ]}
                totalLabel="Totale Scontato" totalValue={formatCurrency(s2_total)} totalColor="text-emerald-600"
                testId="scenario-smart"
              />
              <ScenarioCard
                title="Vantaggio" headerBg="bg-orange-500" badge="-5%"
                description="Massimo risparmio con saldo anticipato."
                saving={formatCurrency(s3_discount)} savingColor="orange"
                rows={[
                  { label: "100% Ordine", value: formatCurrency(s3_total) },
                  { label: "Saldo", value: "€ 0,00", muted: true },
                ]}
                totalLabel="Totale Scontato" totalValue={formatCurrency(s3_total)} totalColor="text-orange-600"
                testId="scenario-vantaggio"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ScenarioCard({ title, headerBg, badge, featured, description, saving, savingColor, rows, totalLabel, totalValue, totalColor, testId }) {
  return (
    <div
      className={`bg-white rounded-xl overflow-hidden border transition-shadow flex flex-col ${
        featured ? "border-2 border-emerald-500 shadow-xl lg:-translate-y-2 z-10" : "border-slate-200 hover:shadow-md"
      }`}
      data-testid={testId}
    >
      <div className={`${headerBg} text-white p-4 text-center relative`}>
        <h3 className="font-outfit font-bold text-lg uppercase tracking-wider">{title}</h3>
        {badge && (
          <div
            className={`absolute top-2 right-2 text-lg font-black px-3 py-1 rounded-full shadow-lg border-4 border-white transform rotate-6 ${
              featured ? "bg-amber-300 text-amber-900 rotate-12 animate-pulse" : "bg-white text-orange-600"
            }`}
          >
            {badge}
          </div>
        )}
      </div>
      <div className="p-6 flex-grow flex flex-col justify-between">
        <p className="text-slate-600 text-sm mb-6 text-center">{description}</p>
        <div className="space-y-3">
          {saving && (
            <div className={`flex justify-between text-xs bg-${savingColor}-50 p-2 rounded border border-${savingColor}-200 text-${savingColor}-700`}>
              <span>Risparmio:</span>
              <span className="font-bold">-{saving}</span>
            </div>
          )}
          {rows.map((r, i) => (
            <div key={i} className={`flex justify-between border-b pb-2 border-slate-100 ${r.muted ? "opacity-40" : ""}`}>
              <span className="text-slate-500 text-sm">{r.label}</span>
              <span className="font-bold text-slate-900 font-mono text-sm">{r.value}</span>
            </div>
          ))}
          <div className="pt-4 text-center bg-slate-50 rounded-lg p-3 mt-2">
            <span className="block text-[10px] text-slate-400 uppercase mb-1 tracking-wider">{totalLabel}</span>
            <span className={`text-2xl font-outfit font-bold ${totalColor}`}>{totalValue}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
